import { useState, useEffect } from 'react';
import { db } from '../firebase-utils';
import {
  collection, query, getDocs, orderBy,
  addDoc, updateDoc, doc, serverTimestamp
} from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// BackingTrackPicker
// Tabs: GigWave Song Bank (Firestore) | Paste Link (direct mp3 or YouTube URL)
// ─────────────────────────────────────────────────────────────────────────────
export default function BackingTrackPicker({ song, onSave, onClose }) {
  const [tab, setTab] = useState('bank');
  const [query_, setQuery_] = useState(song?.title || '');
  const [results, setResults] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [loadingBank, setLoadingBank] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pastedLink, setPastedLink] = useState(
    song?.backingTrackSource === 'link' ? (song?.backingTrackUrl || '') : ''
  );
  const [saving, setSaving] = useState(false);
  const [previewId, setPreviewId] = useState(null); // youtube video id being previewed

  // Load all backing tracks from Firestore on mount
  useEffect(() => {
    const loadBank = async () => {
      setLoadingBank(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'backingTracks'), orderBy('title'))
        );
        const tracks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllTracks(tracks);
        setResults(filterTracks(tracks, song?.title || ''));
      } catch (e) {
        console.error('Error loading backing tracks:', e);
      }
      setLoadingBank(false);
    };
    loadBank();
  }, []);

  const filterTracks = (tracks, q) => {
    if (!q.trim()) return tracks;
    const lower = q.toLowerCase();
    return tracks.filter(t =>
      t.title.toLowerCase().includes(lower) ||
      t.artist.toLowerCase().includes(lower) ||
      (t.genre || '').toLowerCase().includes(lower)
    );
  };

  const handleSearch = (val) => {
    setQuery_(val);
    setResults(filterTracks(allTracks, val));
  };

  // Extract YouTube video ID from various URL formats
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/, // raw ID
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  };

  const pastedYouTubeId = extractYouTubeId(pastedLink);
  const pastedIsMp3 = pastedLink.trim() && !pastedYouTubeId;

  const canSave =
    (tab === 'bank' && selected) ||
    (tab === 'link' && pastedLink.trim());

  const handleSave = async () => {
    setSaving(true);
    if (tab === 'bank' && selected) {
      await onSave({
        backingTrackUrl: selected.youtubeId,
        backingTrackSource: 'youtube',
        backingTrackName: `${selected.title} — ${selected.artist}`,
        backingTrackEnabled: true,
      });
    } else if (tab === 'link') {
      const ytId = extractYouTubeId(pastedLink.trim());
      if (ytId) {
        await onSave({
          backingTrackUrl: ytId,
          backingTrackSource: 'youtube',
          backingTrackName: song?.title ? `${song.title} — ${song.artist || 'Unknown'}` : 'Custom YouTube Link',
          backingTrackEnabled: true,
        });
        // ── Upsert to song bank so everyone benefits ──
        try {
          const snap = await getDocs(query(collection(db, 'backingTracks'), orderBy('title')));
          const existing = snap.docs.find(d => {
            const t = d.data();
            return (
              t.title?.toLowerCase() === song?.title?.toLowerCase() &&
              t.artist?.toLowerCase() === (song?.artist || '').toLowerCase()
            );
          });
          if (existing) {
            // Update pending entry with the YouTube link
            if (!existing.data().youtubeId) {
              await updateDoc(doc(db, 'backingTracks', existing.id), {
                youtubeId: ytId,
                updatedAt: serverTimestamp(),
              });
            }
          } else if (song?.title) {
            // Create new bank entry with link already attached
            await addDoc(collection(db, 'backingTracks'), {
              title: song.title.trim(),
              artist: (song.artist || '').trim(),
              genre: song.genre || null,
              youtubeId: ytId,
              addedByArtist: true,
              addedAt: serverTimestamp(),
            });
          }
        } catch (e) {
          // Non-critical — don't block the save
          console.warn('Could not upsert to song bank:', e.message);
        }
      } else {
        // Plain mp3 link — just save to the song, don't add to bank
        await onSave({
          backingTrackUrl: pastedLink.trim(),
          backingTrackSource: 'link',
          backingTrackName: 'Custom Link',
          backingTrackEnabled: true,
        });
      }
    }
    setSaving(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(135deg,#1a0a2e,#0d1a3a)',
          border: '1px solid rgba(0,245,255,0.25)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <div className="text-white font-black text-base">🎵 Add Backing Track</div>
            <div className="text-cyan-400 text-xs mt-0.5 truncate max-w-xs">{song?.title}</div>
          </div>
          <button onClick={onClose}
            className="text-gray-400 hover:text-white text-xl font-bold w-8 h-8 flex items-center justify-center">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {[['bank', '🎸 Song Bank'], ['link', '🔗 Paste Link']].map(([id, label]) => (
            <button key={id} onClick={() => { setTab(id); setSelected(null); setPreviewId(null); }}
              className="flex-1 py-3 text-sm font-bold transition-all"
              style={{
                color: tab === id ? '#00f5ff' : '#888',
                borderBottom: tab === id ? '2px solid #00f5ff' : '2px solid transparent',
                background: 'transparent',
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">

          {/* ── SONG BANK TAB ── */}
          {tab === 'bank' && (
            <div>
              <input
                value={query_}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by song title or artist..."
                className="w-full rounded-lg px-3 py-2 text-sm text-white mb-3"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(0,245,255,0.2)', outline: 'none' }}
              />

              {loadingBank ? (
                <div className="text-center text-gray-400 text-sm py-8">Loading song bank...</div>
              ) : results.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 text-sm mb-1">No tracks found.</p>
                  <p className="text-gray-500 text-xs">Try the Paste Link tab to add your own YouTube link.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map(track => (
                    <div key={track.id}
                      onClick={() => { setSelected(track); setPreviewId(null); }}
                      className="rounded-lg p-3 cursor-pointer transition-all"
                      style={{
                        background: selected?.id === track.id ? 'rgba(0,245,255,0.12)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${selected?.id === track.id ? 'rgba(0,245,255,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-semibold truncate">{track.title}</div>
                          <div className="text-gray-400 text-xs truncate">{track.artist}
                            {track.genre && <span className="ml-2 text-cyan-600">#{track.genre}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {selected?.id === track.id && (
                            <span className="text-cyan-400 text-xs font-bold">✓</span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setPreviewId(previewId === track.youtubeId ? null : track.youtubeId); }}
                            className="text-xs px-2 py-1 rounded font-bold"
                            style={{ background: 'rgba(0,245,255,0.15)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.3)' }}
                          >
                            {previewId === track.youtubeId ? '⏹' : '▶'}
                          </button>
                        </div>
                      </div>
                      {/* Inline YouTube preview */}
                      {previewId === track.youtubeId && (
                        <div className="mt-2 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                          <iframe
                            width="100%" height="100%"
                            src={`https://www.youtube.com/embed/${track.youtubeId}?autoplay=1`}
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            style={{ border: 'none', display: 'block' }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PASTE LINK TAB ── */}
          {tab === 'link' && (
            <div>
              <p className="text-gray-400 text-xs mb-3">
                Paste a <strong className="text-white">YouTube URL</strong> or a direct <strong className="text-white">.mp3 link</strong>.
              </p>
              <input
                value={pastedLink}
                onChange={e => { setPastedLink(e.target.value); setPreviewId(null); }}
                placeholder="https://youtube.com/watch?v=... or direct mp3 URL"
                className="w-full rounded-lg px-3 py-2 text-sm text-white mb-3"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(0,245,255,0.2)', outline: 'none' }}
              />

              {/* YouTube preview */}
              {pastedYouTubeId && (
                <div>
                  <p className="text-gray-400 text-xs mb-2">Preview:</p>
                  <div className="rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <iframe
                      width="100%" height="100%"
                      src={`https://www.youtube.com/embed/${pastedYouTubeId}`}
                      allow="encrypted-media"
                      allowFullScreen
                      style={{ border: 'none', display: 'block' }}
                    />
                  </div>
                </div>
              )}

              {/* mp3 preview */}
              {pastedIsMp3 && (
                <div>
                  <p className="text-gray-400 text-xs mb-2">Preview:</p>
                  <audio src={pastedLink.trim()} controls className="w-full" style={{ height: 32 }} />
                </div>
              )}

              <div className="mt-3 p-3 rounded-lg text-xs"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#aaa' }}>
                <strong className="text-white">Tip:</strong> Search YouTube for <em>"{song?.title} karaoke"</em> or <em>"{song?.title} backing track"</em>, then paste the URL here.
              </div>
              {pastedYouTubeId && song?.title && (
                <div className="mt-2 p-2 rounded-lg text-xs flex items-center gap-2"
                  style={{ background: 'rgba(0,245,255,0.07)', border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff' }}>
                  🎸 Your link will also be saved to the GigWave Song Bank for other artists.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex gap-3 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-300"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={!canSave || saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-black text-black"
            style={{ background: canSave ? '#00f5ff' : '#333', opacity: saving ? 0.7 : 1, transition: 'all 0.2s' }}>
            {saving ? 'Saving...' : '✓ Save Track'}
          </button>
        </div>
      </div>
    </div>
  );
}
